import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild
} from "@angular/core";

@Component({
  selector: 'app-render-markdown',
  standalone: false,
  templateUrl: './render-markdown.component.html',
  styleUrl: './render-markdown.component.scss'
})
export class RenderMarkdownComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() markdownData: string = ''
  @ViewChild('markdownContainer', { static: false })
  markdownContainer?: ElementRef<HTMLElement>;

  readonly isBrowser: boolean;

  private observer?: MutationObserver;
  private normalizeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly genericLinkTexts = new Set([
    'click here',
    'click this',
    'go',
    'here',
    'this',
    'start',
    'right here',
    'more',
    'learn more',
    'read more',
  ]);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }
    this.startObserver();
    this.scheduleNormalizeLinks();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isBrowser && changes['markdownData']) {
      this.scheduleNormalizeLinks();
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.normalizeTimer) {
      clearTimeout(this.normalizeTimer);
    }
  }

  private startObserver(): void {
    const root = this.markdownContainer?.nativeElement;
    if (!root || typeof MutationObserver === 'undefined') {
      return;
    }

    this.observer = new MutationObserver(() => this.scheduleNormalizeLinks());
    this.observer.observe(root, { childList: true, subtree: true });
  }

  private scheduleNormalizeLinks(): void {
    if (this.normalizeTimer) {
      clearTimeout(this.normalizeTimer);
    }

    this.normalizeTimer = setTimeout(() => this.normalizeLinks(), 0);
  }

  private normalizeLinks(): void {
    const root = this.markdownContainer?.nativeElement;
    if (!root) {
      return;
    }

    const anchors = Array.from(root.querySelectorAll('a'));
    anchors.forEach((anchor) => {
      this.normalizeAnchorHref(anchor);
      this.normalizeAnchorText(anchor);
      this.normalizeAnchorTarget(anchor);
    });
  }

  private normalizeAnchorHref(anchor: HTMLAnchorElement): void {
    const rawHref = String(anchor.getAttribute('href') || '').trim();

    if (!rawHref || rawHref === '#' || rawHref.toLowerCase().startsWith('javascript:')) {
      anchor.removeAttribute('href');
      anchor.setAttribute('role', 'button');
      anchor.setAttribute('tabindex', '0');
      return;
    }

    anchor.removeAttribute('role');
  }

  private normalizeAnchorText(anchor: HTMLAnchorElement): void {
    const text = this.normalizeText(anchor.textContent || '');
    if (!text || !this.isGenericText(text)) {
      return;
    }

    const descriptor = this.describeDestination(anchor.getAttribute('href') || '');
    if (!descriptor) {
      return;
    }

    if (!anchor.querySelector('.seo-link-context')) {
      const contextSpan = this.document.createElement('span');
      contextSpan.className = 'seo-link-context';
      contextSpan.textContent = ` ${descriptor}`;
      anchor.appendChild(contextSpan);
    }

    const currentAria = String(anchor.getAttribute('aria-label') || '').trim();
    if (!currentAria) {
      anchor.setAttribute('aria-label', `${text} ${descriptor}`.trim());
    }
  }

  private normalizeAnchorTarget(anchor: HTMLAnchorElement): void {
    const target = String(anchor.getAttribute('target') || '').trim().toLowerCase();
    if (target !== '_blank') {
      return;
    }

    const rel = String(anchor.getAttribute('rel') || '').toLowerCase();
    const parts = new Set(rel.split(/\s+/).filter(Boolean));
    parts.add('noopener');
    parts.add('noreferrer');
    anchor.setAttribute('rel', Array.from(parts).join(' '));
  }

  private normalizeText(value: string): string {
    return value.replace(/\s+/g, ' ').trim().toLowerCase();
  }

  private isGenericText(value: string): boolean {
    return this.genericLinkTexts.has(value);
  }

  private describeDestination(href: string): string {
    const cleanedHref = String(href || '').trim();
    if (!cleanedHref) {
      return '';
    }

    try {
      const resolved = new URL(cleanedHref, this.document.baseURI || this.document.location?.href || 'http://localhost/');
      const pathSegment = resolved.pathname
        .split('/')
        .filter(Boolean)
        .pop() || '';
      const decoded = decodeURIComponent(pathSegment).replace(/[-_]+/g, ' ').trim();
      if (decoded) {
        return `about ${decoded}`;
      }
      return `at ${resolved.hostname}`;
    } catch {
      return '';
    }
  }
}
